<script setup lang="ts">
import { inject, Ref } from 'vue'
import { computed } from 'vue-demi'
import { useI18n } from 'vue-i18n'
import { Priloha, ZoPPMStruktPodmienkaVyjadrenieEnum } from '/@src/client-portal'
import {
  StrukturovanaPodmienkaSpecRef,
  ZoppmPodmienkaSpecRefTypEnum,
} from '/@src/client-public'
import { useSteps } from '/@src/composable/useSteps'
import { getPodmienkaTitle } from '/@src/utils/zoppm/misc'
import { NazvyKrokov } from '/@src/utils/zoppm/types'
import { richTextToGovukHtml } from '/@src/utils/richTextToGovukHtml'
import { newLineToLineBreak } from '/@src/utils/format'
import { usePrilohaDownload } from './usePrilohaDownload'
import { ZoppmSchemeTransformer } from '/@src/utils/zoppm'
import { NazvyAssemblySekcii } from '/@src/utils/zoppm/ZoppmSchemeTransformer/types'

export interface PodmienkyPoskytnutiaPrispevku {
  podmienkyPoskytnutiaPrispevku: any
  isValidSection: boolean
}

const props = withDefaults(defineProps<PodmienkyPoskytnutiaPrispevku>(), {
  podmienkyPoskytnutiaPrispevku: undefined,
  isValidSection: false,
})

const { t } = useI18n()
const steps = useSteps()

const kodIrz = inject<Ref<string>>('kodIrz')
const stavPodana = inject<boolean>('stavPodana', false)
const bearerTokenIS = inject<string | undefined>('bearerTokenIS')
const ziadostId = inject<Ref<string | undefined>>('ziadostId')

const podmienkyOrder = [
  ZoppmPodmienkaSpecRefTypEnum['PodmienkyOpravnenostiZiadatela'],
  ZoppmPodmienkaSpecRefTypEnum['PodmienkyDoruceniaZiadosti'],
  ZoppmPodmienkaSpecRefTypEnum['PodmienkyStatnejPomoci'],
  ZoppmPodmienkaSpecRefTypEnum['DalsiePodmienkyZOsobitnychPredpisov'],
  ZoppmPodmienkaSpecRefTypEnum['DalsiePodmienkyPoskytnutiaProstriedkovMechanizmu'],
  ZoppmPodmienkaSpecRefTypEnum['UcelPouzitiaProstriedkovMechanizmu'],
]

const pomienkySpec: any = {
  PODMIENKY_OPRAVNENOSTI_ZIADATELA: 'zoppm.ks.podmienky.opravnenost-ziadatela',
  DALSIE_PODMIENKY_POSKYTNUTIA_PROSTRIEDKOV_MECHANIZMU: 'zoppm.ks.podmienky.dalsie',
  PODMIENKY_DORUCENIA_ZIADOSTI: 'zoppm.ks.podmienky.dorucenie-ziadosti',
  PODMIENKY_STATNEJ_POMOCI: 'zoppm.ks.podmienky.statna-pomoc',
  DALSIE_PODMIENKY_Z_OSOBITNYCH_PREDPISOV: 'zoppm.ks.podmienky.osobitne-predpisy',
  UCEL_POUZITIA_PROSTRIEDKOV_MECHANIZMU: 'zoppm.ks.podmienky.ucel-pouzitia',
}

const podmienkyData = computed(() =>
  [...props?.podmienkyPoskytnutiaPrispevku].sort(function (a, b) {
    return (
      podmienkyOrder.indexOf(a?.zoppmPodmienkaSpecRef?.typ) -
      podmienkyOrder.indexOf(b?.zoppmPodmienkaSpecRef?.typ)
    )
  })
)

const getDokument = usePrilohaDownload({
  stavPodana,
  ziadostId: ziadostId?.value,
  bearerTokenIS,
  kodIrz: kodIrz?.value,
})

function getPriloha(
  zoppmStruktPodmienkaSpecRef: StrukturovanaPodmienkaSpecRef,
  prilohy: Priloha[]
) {
  return prilohy?.find((priloha: any) => {
    return (
      priloha?.priloha?.lokalnaReferencia?.refId?.toString() ===
      zoppmStruktPodmienkaSpecRef?.zoppmStruktPodmienkaSpecId?.toString()
    )
  })?.priloha
}

function getVyjadrenie(vyjadrenie: ZoPPMStruktPodmienkaVyjadrenieEnum) {
  switch (vyjadrenie) {
    case ZoPPMStruktPodmienkaVyjadrenieEnum['Ano']:
      return t('zoppm.step2.splnam')
    case ZoPPMStruktPodmienkaVyjadrenieEnum['Nie']:
      return t('zoppm.step2.nesplnam')
    case ZoPPMStruktPodmienkaVyjadrenieEnum['NetykaSa']:
      return t('zoppm.step2.netykaSa')
    default:
      return vyjadrenie
  }
}

const schemeTransformer = inject<Ref<ZoppmSchemeTransformer>>('schemeTransformer')
const sekciaRefNazov: NazvyAssemblySekcii = 'zoppm.ks.podmienky'
const heading = computed(
  () =>
    schemeTransformer?.value.getSectionTitle(sekciaRefNazov) ||
    t('zoppm.zhrnutie.podmienkyPoskytnutiaPrispevku.mainHeading')
)
</script>

<template>
  <IDSKHeading as="h2" variant="l" class="mb-0">
    {{ heading }}
  </IDSKHeading>

  <ZhrnutieError v-if="isValidSection" class="mt-6" />

  <template v-for="(podmienka, key) in podmienkyData" :key="key">
    <template
      v-if="
        podmienka?.prilohy?.some((priloha) => priloha?.priloha?.dokumenty?.length > 0) ||
        podmienka?.vyjadrenie ||
        podmienka?.strukturovanePodmienky?.length > 0
      "
    >
      <IDSKHeading as="h3" variant="m" class="mb-6 mt-7">
        {{
          schemeTransformer?.getSectionTitle(
            pomienkySpec[podmienka?.zoppmPodmienkaSpecRef?.typ]
          ) || t('zoppm.zhrnutie.podmienkyPoskytnutiaPrispevku.mainHeading')
        }}
      </IDSKHeading>
      <div
        class="mb-6"
        v-html="richTextToGovukHtml(podmienka?.zoppmPodmienkaSpecRef?.podmienka)"
      />
    </template>

    <div v-if="podmienka?.vyjadrenie">
      <IDSKHeading as="h4" variant="s" class="mb-2">
        {{ t('zoppm.step2.vyjadrenie') }}
      </IDSKHeading>
      <IDSKParagraph v-html="newLineToLineBreak(podmienka?.vyjadrenie)" />
    </div>

    <div v-if="podmienka?.prilohy?.length > 0 && podmienka?.vyjadrenie">
      <IDSKHeading as="h4" variant="s" class="mb-2">
        {{ t('globalne.prilohy') }}
      </IDSKHeading>

      <template
        v-for="dokument in podmienka.prilohy?.[0]?.priloha?.dokumenty"
        :key="dokument"
      >
        <IDSKLink
          href="#"
          class="govuk-!-display-block"
          no-visited-state
          @click.prevent="
            getDokument(
              dokument.dokumentRef.identifikatorUlozisko,
              dokument.dokumentRef.verziaUlozisko
            )
          "
        >
          {{ `${dokument.nazov}.${dokument.format}` }}
        </IDSKLink>
      </template>
    </div>

    <template v-for="(item, itemKey) in podmienka?.strukturovanePodmienky" :key="itemKey">
      <div>
        <IDSKHeading as="h4" variant="s" class="mb-2">
          {{ itemKey + 1 }}. {{ item.zoppmStruktPodmienkaSpecRef?.nazov }}
        </IDSKHeading>
        <IDSKParagraph>
          {{ getVyjadrenie(item.vyjadrenie) }}
        </IDSKParagraph>

        <div v-if="item.poznamka">
          <IDSKHeading as="h5" variant="s" class="mb-2">
            {{ t('globalne.poznamka') }}
          </IDSKHeading>
          <IDSKParagraph v-html="newLineToLineBreak(item?.poznamka)" />
        </div>

        <div
          v-if="
            getPriloha(item.zoppmStruktPodmienkaSpecRef, podmienka.prilohy)?.dokumenty
              ?.length > 0
          "
          :class="{ 'mb-6': itemKey !== podmienka?.strukturovanePodmienky?.length - 1 }"
        >
          <IDSKHeading as="h5" variant="s" class="mb-2">
            {{ t('globalne.prilohy') }}
          </IDSKHeading>
          <template
            v-for="dokument in getPriloha(
              item.zoppmStruktPodmienkaSpecRef,
              podmienka.prilohy
            )?.dokumenty"
            :key="dokument"
          >
            <IDSKLink
              href="#"
              class="govuk-!-display-block"
              no-visited-state
              @click.prevent="
                getDokument(
                  dokument.dokumentRef.identifikatorUlozisko,
                  dokument.dokumentRef.verziaUlozisko
                )
              "
            >
              {{ `${dokument.nazov}.${dokument.format}` }}
            </IDSKLink>
          </template>
        </div>
      </div>
    </template>
  </template>

  <div v-if="!stavPodana" class="d-flex align-items-right">
    <IDSKButton
      variant="secondary"
      type="button"
      class="mb-0"
      :data-test-id="`zoppm-ppp-edit`"
      @click="
        () =>
          steps.setStep(NazvyKrokov['podmienkyPoskytnutiaPrispevku'], {
            ref: NazvyKrokov['zhrnutie'],
          })
      "
    >
      {{ t('zoppm.zhrnutie.editBtn') }}
    </IDSKButton>
  </div>

  <hr class="mb-8 mt-8" />
</template>
